import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import AdminGuard from "../components/AdminGuard";
import AdminLayout from "../components/AdminLayout";
import { useAdminSession } from "../components/AdminSessionContext";
import { supabase } from "@/lib/supabase";
import {
  IMPORT_COLUMNS,
  downloadTemplate,
  parseTextContent,
  parseWorkbookFile,
  type ParseResult,
  type ParsedRow,
} from "./importConfig";

const PREVIEW_LIMIT = 50;
const INSERT_CHUNK = 100;
const DUP_CHUNK = 200;

interface ImportOutcome {
  inserted: number;
  failed: number;
  ok: boolean;
  message: string;
}

function rowBadge(row: ParsedRow, duplicate: boolean) {
  if (row.errors.length) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
        <i className="ri-close-circle-line"></i>
        {row.errors[0]}
      </span>
    );
  }
  if (duplicate) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
        <i className="ri-alert-line"></i>
        手机号已存在
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
      <i className="ri-check-line"></i>
      可导入
    </span>
  );
}

function ImportContent() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { username } = useAdminSession();
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [result, setResult] = useState<ParseResult | null>(null);
  const [duplicatePhones, setDuplicatePhones] = useState<Set<string>>(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const isDuplicate = (row: ParsedRow) => {
    const phone = String(row.data.phone ?? "").trim();
    return phone.length > 0 && duplicatePhones.has(phone);
  };

  const insertable = (result?.rows || []).filter(
    (row) => row.errors.length === 0 && !(skipDuplicates && isDuplicate(row)),
  );

  const resetAll = () => {
    setFileName("");
    setResult(null);
    setParseError("");
    setOutcome(null);
    setDuplicatePhones(new Set());
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyParsed = async (parsed: ParseResult) => {
    setResult(parsed);
    const phones = Array.from(
      new Set(
        parsed.rows
          .map((row) => String(row.data.phone ?? "").trim())
          .filter((phone) => phone.length > 0),
      ),
    );
    if (phones.length > 0) {
      const found = new Set<string>();
      for (let i = 0; i < phones.length; i += DUP_CHUNK) {
        const chunk = phones.slice(i, i + DUP_CHUNK);
        const { data } = await supabase.from("match_profiles").select("phone").in("phone", chunk);
        (data || []).forEach((item) => {
          if (item.phone) found.add(String(item.phone));
        });
      }
      setDuplicatePhones(found);
    } else {
      setDuplicatePhones(new Set());
    }
  };

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setParsing(true);
    setParseError("");
    setResult(null);
    setOutcome(null);
    setDuplicatePhones(new Set());

    try {
      const parsed = await parseWorkbookFile(file);
      await applyParsed(parsed);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "文件解析失败，请检查文件格式");
    } finally {
      setParsing(false);
    }
  };

  const handleParseText = async () => {
    const text = pasteText.trim();
    if (!text) {
      setParseError("请先把嘉宾信息粘贴到下面的框里");
      return;
    }
    setFileName("");
    setParsing(true);
    setParseError("");
    setResult(null);
    setOutcome(null);
    setDuplicatePhones(new Set());
    try {
      const parsed = parseTextContent(text);
      if (parsed.rows.length === 0) {
        setParseError("没识别到可用的字段，试试「字段名：值」一行一项的写法");
        return;
      }
      await applyParsed(parsed);
    } catch {
      setParseError("识别失败，请检查粘贴内容");
    } finally {
      setParsing(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleImport = async () => {
    if (insertable.length === 0) return;
    setImporting(true);
    setOutcome(null);
    let inserted = 0;
    let failed = 0;
    let lastError = "";

    try {
      for (let i = 0; i < insertable.length; i += INSERT_CHUNK) {
        const chunk = insertable.slice(i, i + INSERT_CHUNK).map((row) => {
          const payload: Record<string, unknown> = {};
          IMPORT_COLUMNS.forEach((col) => {
            payload[col.field] = row.data[col.field] ?? null;
          });
          // 数据库里 name / gender / phone 不允许为空，识别不到时补默认值，保证「有什么填什么」也能入库
          if (!payload.name) payload.name = "未填写";
          if (!payload.gender) payload.gender = "男";
          if (payload.phone === null || payload.phone === undefined) payload.phone = "";
          payload.status = "pending";
          payload.created_by_name = username || null;
          return payload;
        });
        const { error } = await supabase.from("match_profiles").insert(chunk);
        if (error) {
          failed += chunk.length;
          lastError = error.message;
        } else {
          inserted += chunk.length;
        }
      }

      if (failed === 0) {
        setOutcome({
          inserted,
          failed,
          ok: true,
          message: `成功导入 ${inserted} 条会员资料，可前往会员列表查看。`,
        });
        setResult(null);
        setFileName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setOutcome({
          inserted,
          failed,
          ok: false,
          message: `成功导入 ${inserted} 条，失败 ${failed} 条。${
            lastError ? `原因：${lastError}` : "请检查数据后重试。"
          }`,
        });
      }
    } catch (err) {
      setOutcome({
        inserted,
        failed: insertable.length - inserted,
        ok: false,
        message: err instanceof Error ? err.message : "导入过程中出现异常，请稍后重试",
      });
    } finally {
      setImporting(false);
    }
  };

  const duplicateCount = result
    ? result.rows.filter((row) => row.errors.length === 0 && isDuplicate(row)).length
    : 0;
  const previewRows = result ? result.rows.slice(0, PREVIEW_LIMIT) : [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            一键导入会员资料
          </h1>
          <p className="mt-1 text-sm text-foreground-500">
            上传 Excel 或 CSV 文件，支持标准表格和「字段名：值」卡片式资料，系统会自动识别并入库
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-file-download-line"></i>
            下载导入模板
          </button>
          <Link
            to="/admin"
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回列表
          </Link>
        </div>
      </div>

      {outcome && (
        <div
          className={`flex items-start gap-3 rounded-lg border px-5 py-4 ${
            outcome.ok
              ? "border-primary-200 bg-primary-50 text-primary-800"
              : "border-secondary-200 bg-secondary-50 text-secondary-900"
          }`}
        >
          <i className={`mt-0.5 ${outcome.ok ? "ri-checkbox-circle-line" : "ri-alert-line"}`}></i>
          <div>
            <p className="text-sm font-medium">{outcome.ok ? "导入完成" : "导入部分完成"}</p>
            <p className="mt-1 text-xs leading-relaxed">{outcome.message}</p>
          </div>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors ${
          dragging
            ? "border-primary-400 bg-primary-50"
            : "border-background-300 bg-background-50"
        }`}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-upload-cloud-2-line text-2xl"></i>
        </span>
        <p className="mt-4 text-sm font-medium text-foreground-800">
          拖拽文件到此处，或点击选择文件
        </p>
        <p className="mt-1 text-xs text-foreground-400">支持 .xlsx / .xls / .csv 格式</p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-folder-open-line"></i>
            选择文件
          </button>
          {fileName && (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-refresh-line"></i>
              重新选择
            </button>
          )}
        </div>
        {fileName && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
            <i className="ri-file-excel-2-line text-primary-500"></i>
            {fileName}
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-clipboard-line text-sm"></i>
          </span>
          <h2 className="font-heading text-sm font-semibold text-foreground-900">
            或直接粘贴嘉宾信息
          </h2>
          <span className="text-xs text-foreground-400">（一行一项，如「身高：176」）</span>
        </div>
        <textarea
          rows={4}
          value={pasteText}
          onChange={(e) => {
            setPasteText(e.target.value);
            if (parseError) setParseError("");
          }}
          placeholder={"把嘉宾资料整段粘贴到这里，例如：\n姓名：李先生\n出生年月：1967年\n身高：176CM\n婚姻状况：离异\n年收入：30万左右"}
          className="mt-3 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        ></textarea>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleParseText}
            disabled={parsing}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-magic-line"></i>
            自动识别
          </button>
        </div>
      </div>

      {!result && !parsing && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-sm font-semibold text-foreground-900">
            表格需要包含的列
          </h2>
          <p className="mt-1 text-xs text-foreground-400">
            标准表格：列顺序不限，列名与下表一致即可。卡片式：每行写「字段名：值」，如「出生年月：1967年」，能认多少填多少。
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {IMPORT_COLUMNS.map((col) => (
              <span
                key={col.field}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                  col.required
                    ? "bg-accent-100 text-accent-800"
                    : "bg-background-100 text-foreground-600"
                }`}
              >
                {col.required && <i className="ri-asterisk text-[10px]"></i>}
                {col.header}
              </span>
            ))}
          </div>
        </div>
      )}

      {parsing && (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-50 py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
          正在解析并比对资料…
        </div>
      )}

      {parseError && (
        <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{parseError}</span>
        </div>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs text-foreground-400">读取数据行</p>
              <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                {result.rows.length}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs text-foreground-400">校验通过</p>
              <p className="mt-1 font-heading text-2xl font-bold text-primary-600">
                {result.validCount}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs text-foreground-400">数据有误</p>
              <p className="mt-1 font-heading text-2xl font-bold text-accent-600">
                {result.errorCount}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs text-foreground-400">手机号重复</p>
              <p className="mt-1 font-heading text-2xl font-bold text-secondary-600">
                {duplicateCount}
              </p>
            </div>
          </div>

          {result.mode === "card" && (
            <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
              <i className="ri-magic-line mt-0.5 text-primary-600"></i>
              <p className="text-xs leading-relaxed text-primary-800">
                已按「卡片式资料」识别（逐行读取「字段名：值」），能对上的字段会自动填入。
              </p>
            </div>
          )}

          {result.skippedHeaders.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
              <i className="ri-information-line mt-0.5 text-secondary-600"></i>
              <p className="text-xs leading-relaxed text-secondary-900">
                以下内容未被识别，导入时会自动忽略：
                <span className="font-medium">{result.skippedHeaders.join("、")}</span>。如有需要请参照模板调整字段名。
              </p>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex flex-col gap-3 border-b border-background-200 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-heading text-sm font-semibold text-foreground-900">
                数据预览
                <span className="ml-2 text-xs font-normal text-foreground-400">
                  仅显示前 {PREVIEW_LIMIT} 行
                </span>
              </h2>
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-foreground-600">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-primary-500"
                />
                跳过手机号已存在的资料
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100 text-left text-xs font-medium text-foreground-500">
                    <th className="px-4 py-3 font-medium">行</th>
                    <th className="px-4 py-3 font-medium">姓名</th>
                    <th className="px-4 py-3 font-medium">性别</th>
                    <th className="px-4 py-3 font-medium">年龄</th>
                    <th className="px-4 py-3 font-medium">手机号</th>
                    <th className="px-4 py-3 font-medium">现居城市</th>
                    <th className="px-4 py-3 font-medium">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => {
                    const duplicate = row.errors.length === 0 && isDuplicate(row);
                    return (
                      <tr
                        key={`import-row-${index}`}
                        className="border-b border-background-100 last:border-b-0"
                      >
                        <td className="px-4 py-3 text-xs text-foreground-400">{index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground-900">
                          {row.data.name || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-700">
                          {row.data.gender || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-700">
                          {row.data.age ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-700">
                          {row.data.phone || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground-700">
                          {row.data.residence || "—"}
                        </td>
                        <td className="px-4 py-3">{rowBadge(row, duplicate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-4 sm:flex-row sm:items-center">
            <p className="text-sm text-foreground-600">
              本次将导入{" "}
              <span className="font-semibold text-primary-600">{insertable.length}</span> 条资料
              {skipDuplicates && duplicateCount > 0 && (
                <span className="text-foreground-400">（已排除 {duplicateCount} 条重复）</span>
              )}
            </p>
            <button
              type="button"
              onClick={handleImport}
              disabled={importing || insertable.length === 0}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importing ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-database-2-line"></i>
              )}
              {importing ? "正在导入…" : `确认导入 ${insertable.length} 条`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminImport() {
  return (
    <AdminGuard allow={["admin", "matchmaker"]}>
      <AdminLayout>
        <ImportContent />
      </AdminLayout>
    </AdminGuard>
  );
}